import { h } from '../../../src/ui/dom';
import { placeHref } from '../nav';
import { DESTINATIONS, planByDestination, type Destination, type DestinationPlan } from '../places/destinations';
import { getJson, span, type PlanData } from '../view';

/** Where the published plan comes from. */
export const VISITS_URL = '/api/visits';
export type Visits = Awaited<ReturnType<typeof getJson<PlanData>>>;

/** The stops of a country in order, each named once: "Tanna · Port Vila · Luganville". */
export const stopNames = (page: DestinationPlan) => [...new Set(page.stops.map(s => s.label))].join(' · ');

function card(d: Destination, i: number, level: 'h2' | 'h3', page: DestinationPlan | null, published: boolean): HTMLElement {
  const when = page ? [h('p', { class: 'pc-when' }, span(page.from, page.to)), h('p', { class: 'pc-stops' }, stopNames(page))]
    : [h('p', { class: 'pc-when quiet' }, published ? 'Not in the current plan' : 'Dates when the plan is published')];
  return h('a', { class: 'place-card', href: placeHref(d.slug) },
    h('div', { class: 'pc-photo' },
      d.hero ? h('img', { src: d.hero.file, alt: d.hero.alt, loading: i < 3 ? 'eager' : 'lazy' }) : null,
      h('span', { class: 'pc-num' }, String(i + 1).padStart(2, '0')),
      h('div', { class: 'pc-name' }, h(level, {}, d.name), h('p', {}, d.country))),
    h('div', { class: 'pc-body' }, h('p', {}, d.tagline), ...when));
}

/**
 * One card per country in the live plan, in sailing order, with its dates and stops. Before a plan is
 * published every country shows; a stop with no page yet is listed under the cards rather than dropped.
 */
export function placeGrid(visits: Visits, level: 'h2' | 'h3' = 'h2'): HTMLElement {
  if (!visits.ok) return h('div', { class: 'place-grid' }, ...DESTINATIONS.map((d, i) => card(d, i, level, null, false)));
  const { pages, unplaced } = planByDestination(visits.data.stops, visits.data.windows);
  return h('div', { class: 'place-list' },
    h('div', { class: 'place-grid' }, ...pages.map((p, i) => card(p.dest, i, level, p, true))),
    unplaced.length ? h('p', { class: 'notice' }, `Also on the plan, with no page here yet: ${unplaced.map(s => `${s.label} (${span(s.arrive, s.depart)})`).join(', ')}.`) : null);
}

/** The places guide: a header over the country cards. */
export async function renderPlaces(app: HTMLElement): Promise<void> {
  const visits = await getJson<PlanData>(VISITS_URL);
  const dests = visits.ok ? planByDestination(visits.data.stops, visits.data.windows).pages.map(p => p.dest) : DESTINATIONS;
  const lede = `${dests.length} countries in the order we'll sail them, ${dests[0]?.name} to ${dests[dests.length - 1]?.name}. Open one to see every island we stop at and when we're there, and to reserve a spot.`;
  app.replaceChildren(...[
    h('header', { class: 'places-head' }, h('h1', {}, 'The places we’ll be'), h('p', { class: 'lede' }, lede)),
    !visits.ok && visits.status !== 404 ? h('p', { class: 'err' }, `Dates could not load: ${visits.error}`) : null,
    placeGrid(visits),
  ].filter((n): n is HTMLElement => n !== null));
}
